import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from "@material-ui/core";
import { LoadingButton } from "@material-ui/lab";
import { alpha, useTheme } from "@material-ui/core/styles";
import { Add, ArrowBack, DeleteOutline, EditOutlined, Layers } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import useLocationGroups from "hooks/useLocationGroups";
import { summarizeGroupLocations } from "utils/locations/locationGroups";
import { LocationGroupEditor } from "./LocationGroupEditor";
import type { LocationGroup } from "../../../@types/sharedSchema";

/**
 * The list / create / edit / delete body, shared by the manager dialog (the cog
 * on the picker) and the settings card.
 *
 * ── The editor replaces the list; it does not open on top of it ──────────────
 *
 * The editor hosts a `LocationPicker`, whose suggestion `Popper` is portalled to
 * `document.body` at `zIndex: 1300` — the same stacking level as MUI's default
 * modal. Inside the manager DIALOG a nested editor dialog would therefore either
 * cover the suggestions or be covered by them, depending on mount order. Swapping
 * the body in place has one z-index, one back affordance, and no way to end up
 * with two modals arguing about which is on top.
 *
 * Only the delete confirmation is a dialog, and it is one deliberately: a
 * destructive action needs to name what it is about to destroy, which
 * `window.confirm` cannot do in the app's own voice (or its own direction).
 */

type LocationGroupsManagerProps = {
  /** Tighter spacing for the dialog, where the surrounding chrome already pays for padding. */
  dense?: boolean;
  /**
   * When set, each row is also a PICK — the row body reads and behaves as a
   * button. Absent (the settings card) it is a pure management list, and nothing
   * about the row invites a click.
   */
  onPickGroup?: (group: LocationGroup) => void;
  /**
   * Told whenever the body swaps to the editor and back. The editor owns its own
   * Cancel/Save pair, so a host with chrome of its own — the dialog's pinned
   * footer — has to be able to get out of its way while it is open.
   *
   * Pass a `useState` setter, not an inline lambda: this fires from an effect, and
   * a fresh identity every render re-runs it every render. Setting the same
   * boolean twice is a React bail-out, so nothing loops either way, but the stable
   * setter keeps it to one call per actual change.
   */
  onEditingChange?: (editing: boolean) => void;
  /**
   * Open straight into this group's editor instead of the list — how a click on a
   * group CHIP gets here, where the realtor has already named the group they mean
   * and a list would be a step backwards.
   *
   * Read once the group is available rather than on mount: `useLocationGroups`
   * arrives from a snapshot, so on a cold open the id is known before the group
   * is. The back arrow still returns to the list, so this is an entry point, not
   * a mode.
   */
  initialEditGroupId?: string;
  /**
   * Forwarded straight from the open editor: whether it holds edits a close would
   * discard. The manager itself is never dirty — the list has nothing to lose —
   * so it only relays.
   *
   * Pass a `useState` setter for the same reason as `onEditingChange`.
   */
  onDirtyChange?: (dirty: boolean) => void;
};

export function LocationGroupsManager({
  dense,
  onPickGroup,
  onEditingChange,
  initialEditGroupId,
  onDirtyChange
}: LocationGroupsManagerProps): JSX.Element {
  const theme = useTheme();
  const { t } = useTranslation();
  const { groups, loading, error, canAddGroup, deleteGroup } = useLocationGroups();

  // `undefined` inside the object = create; the object's absence = the list.
  const [editing, setEditing] = useState<{ group?: LocationGroup } | null>(null);
  // Which id the auto-open has already been honoured for. Without it, hitting the
  // back arrow would be undone by the very next render.
  const [autoOpenedFor, setAutoOpenedFor] = useState<string | null>(null);

  useEffect(() => {
    if (!initialEditGroupId || autoOpenedFor === initialEditGroupId) return;

    const target = groups.find((group) => group.id === initialEditGroupId);
    if (!target) return; // still loading, or the group was deleted elsewhere

    setAutoOpenedFor(initialEditGroupId);
    setEditing({ group: target });
  }, [initialEditGroupId, autoOpenedFor, groups]);
  const [pendingDelete, setPendingDelete] = useState<LocationGroup | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteErrorKey, setDeleteErrorKey] = useState<string | null>(null);

  useEffect(() => {
    onEditingChange?.(Boolean(editing));
    // Closing the host while the editor is open unmounts this without ever
    // reporting the way back down, and the host would reopen with its chrome
    // still hidden.
    return () => onEditingChange?.(false);
  }, [editing, onEditingChange]);

  const handleDelete = async (): Promise<void> => {
    if (!pendingDelete) return;

    setDeleting(true);
    setDeleteErrorKey(null);
    try {
      await deleteGroup(pendingDelete.id);
      setPendingDelete(null);
    } catch (err) {
      setDeleteErrorKey((err as Error)?.message || "locationGroups.errors.deleteFailed");
    } finally {
      setDeleting(false);
    }
  };

  if (editing) {
    return (
      <Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <IconButton size="small" onClick={() => setEditing(null)}>
            {/* stylis-plugin-rtl rewrites CSS, not glyphs, so a directional icon
                has to be turned by hand — and only in RTL, since the same app
                renders LTR for an English user. */}
            <ArrowBack
              fontSize="small"
              sx={{ transform: theme.direction === "rtl" ? "scaleX(-1)" : "none" }}
            />
          </IconButton>
          <Typography sx={{ fontWeight: 800 }}>
            {editing.group ? t("locationGroups.edit") : t("locationGroups.create")}
          </Typography>
        </Stack>
        <LocationGroupEditor
          group={editing.group}
          onCancel={() => setEditing(null)}
          onSaved={() => setEditing(null)}
          onDirtyChange={onDirtyChange}
        />
      </Box>
    );
  }

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 160 }}>
        <CircularProgress size={26} />
      </Stack>
    );
  }

  if (!groups.length) {
    return (
      <Stack
        alignItems="center"
        spacing={1.25}
        sx={{
          px: 3,
          py: dense ? 3 : 5,
          textAlign: "center",
          borderRadius: "16px",
          border: "1px dashed",
          borderColor: alpha(theme.palette.warning.main, 0.48),
          backgroundColor: alpha(theme.palette.warning.main, 0.06)
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "18px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "warning.dark",
            backgroundColor: alpha(theme.palette.warning.main, 0.16)
          }}
        >
          <Layers />
        </Box>
        <Typography sx={{ fontWeight: 800, fontSize: 16 }}>
          {t("locationGroups.empty.title")}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 420 }}>
          {t("locationGroups.empty.body")}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setEditing({})}
          sx={{ mt: 0.5, fontWeight: 800, borderRadius: "12px", px: 3 }}
        >
          {t("locationGroups.createFirst")}
        </Button>
        {error && <ListenerError error={error} />}
      </Stack>
    );
  }

  return (
    <Box>
      {error && (
        <Box sx={{ mb: 1.5 }}>
          <ListenerError error={error} />
        </Box>
      )}

      <Stack spacing={dense ? 1 : 1.25}>
        {groups.map((group) => {
          const pickable = Boolean(onPickGroup);

          return (
            <Box
              key={group.id}
              role={pickable ? "button" : undefined}
              tabIndex={pickable ? 0 : undefined}
              onClick={pickable ? () => onPickGroup(group) : undefined}
              /* The row is a div rather than a `ButtonBase` because it CONTAINS
                 two buttons and a button inside a button is invalid markup — so
                 the keyboard half of `role="button"` has to be supplied here.

                 `event.target !== event.currentTarget` IS THE WHOLE GUARD. Without
                 it this handler also receives the Enter/Space that a keyboard user
                 aimed at the row's own ✏️ / 🗑️ buttons, since those bubble; it
                 would then `preventDefault()` — cancelling the button's native
                 activation, so edit and delete become unreachable from the keyboard
                 entirely — and pick the group instead, merging up to fifty areas
                 and closing the dialog. `defaultPrevented` alone cannot cover it: a
                 native <button> does not preventDefault its own Enter. */
              onKeyDown={
                pickable
                  ? (event) => {
                      if (event.target !== event.currentTarget || event.defaultPrevented) return;
                      if (event.key !== "Enter" && event.key !== " ") return;
                      event.preventDefault();
                      onPickGroup(group);
                    }
                  : undefined
              }
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: dense ? 1.25 : 1.5,
                borderRadius: "14px",
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
                transition: "border-color .18s, box-shadow .18s, background-color .18s",
                cursor: pickable ? "pointer" : "default",
                ...(pickable && {
                  "&:hover, &:focus-visible": {
                    outline: "none",
                    borderColor: "primary.main",
                    backgroundColor: alpha(theme.palette.primary.main, 0.06),
                    boxShadow: `0 8px 18px -12px ${alpha(theme.palette.primary.main, 0.6)}`
                  }
                })
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  flex: "none",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 20,
                  lineHeight: 1,
                  color: "warning.dark",
                  backgroundColor: alpha(theme.palette.warning.main, 0.16)
                }}
              >
                {group.emoji || <Layers sx={{ width: 20, height: 20 }} />}
              </Box>

              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography noWrap sx={{ fontWeight: 700, fontSize: 15 }}>
                  {group.name}
                </Typography>
                <Typography
                  noWrap
                  variant="caption"
                  sx={{ display: "block", color: "text.secondary" }}
                >
                  {t("locationGroups.groupCount", { count: group.locations?.length ?? 0 })}
                  {summarizeGroupLocations(group) ? ` · ${summarizeGroupLocations(group)}` : ""}
                </Typography>
              </Box>

              <Tooltip title={t("locationGroups.edit") as string}>
                <IconButton
                  size="small"
                  /* The row itself may be the pick target, so every control on it
                     has to stop the click reaching it — otherwise "edit" also
                     picks the group and closes the manager under the editor. */
                  onClick={(event) => {
                    event.stopPropagation();
                    setEditing({ group });
                  }}
                >
                  <EditOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("locationGroups.delete") as string}>
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    setDeleteErrorKey(null);
                    setPendingDelete(group);
                  }}
                >
                  <DeleteOutline fontSize="small" sx={{ color: "error.main" }} />
                </IconButton>
              </Tooltip>
            </Box>
          );
        })}
      </Stack>

      <Stack direction="row" sx={{ mt: dense ? 1.5 : 2 }}>
        <Box sx={{ flex: 1 }} />
        <Tooltip title={canAddGroup ? "" : (t("locationGroups.errors.tooManyGroups") as string)}>
          {/* A disabled button fires no events, so the tooltip needs a live
              wrapper — and the tooltip is the only place the cap is explained. */}
          <Box component="span">
            <Button
              variant="outlined"
              startIcon={<Add />}
              disabled={!canAddGroup}
              onClick={() => setEditing({})}
              sx={{ fontWeight: 700, borderRadius: "12px" }}
            >
              {t("locationGroups.create")}
            </Button>
          </Box>
        </Tooltip>
      </Stack>

      <Dialog
        open={Boolean(pendingDelete)}
        maxWidth="xs"
        fullWidth
        onClose={() => !deleting && setPendingDelete(null)}
        /* A modal's keystrokes are never the host's business. React bubbles
           synthetic events out of a portal along the COMPONENT tree, so without
           this the Enter that confirms a deletion also reaches whatever rendered
           the manager — the CRM cell editor reads it as "commit and close" and
           tears the surface down mid-delete. MUI chains this before its own
           Escape handling, so Escape still closes the dialog. */
        onKeyDown={(event) => event.stopPropagation()}
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 800, mb: 0.5 }}>
            {t("locationGroups.deleteConfirm.title")}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {t("locationGroups.deleteConfirm.body", { name: pendingDelete?.name ?? "" })}
          </Typography>
          {deleteErrorKey && (
            <Typography
              variant="caption"
              sx={{ display: "block", mt: 1.5, color: "error.main", fontWeight: 700 }}
            >
              {t(deleteErrorKey, { defaultValue: deleteErrorKey })}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            color="inherit"
            disabled={deleting}
            onClick={() => setPendingDelete(null)}
            sx={{ color: "text.secondary", fontWeight: 700 }}
          >
            {t("locationGroups.deleteConfirm.cancel")}
          </Button>
          <Box sx={{ flex: 1 }} />
          <LoadingButton
            variant="contained"
            color="error"
            loading={deleting}
            onClick={handleDelete}
            sx={{ fontWeight: 800, borderRadius: "12px" }}
          >
            {t("locationGroups.deleteConfirm.confirm")}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/**
 * The snapshot listener failing is rare and has no key of its own, so it renders
 * whatever the hook handed over: an i18n key resolves, a raw Firestore message
 * falls through `defaultValue` and is shown as-is. Silence would be worse — an
 * empty list and a broken list look identical.
 */
function ListenerError({ error }: { error: string }): JSX.Element {
  const { t } = useTranslation();

  return (
    <Typography variant="caption" sx={{ display: "block", color: "error.main", fontWeight: 700 }}>
      {t(error, { defaultValue: error })}
    </Typography>
  );
}
